import chatService from './chatService';
import { sendChatMessage } from './chatChannelService';

/**
 * Deliver one chat message: over the joined `chat:<id>` topic first, over REST
 * when the socket could not carry it.
 *
 * The WS push fails in two ways that say nothing about the message itself:
 *  - `not joined` — no socket, or the conversation's topic is not joined right
 *    now (reconnecting after sleep, a region failover);
 *  - `timeout` — Phoenix gave up after 10 s without a reply, typically a socket
 *    that still looks open but is dead until the next heartbeat notices.
 * Both used to mark the message failed (the red "!") although the REST route
 * reaches any conversation the user belongs to. The send now takes it, under
 * the SAME uid — the server is idempotent on uid, so a push that did land
 * before timing out is not duplicated (the retry button relies on the same).
 *
 * A reply from the server (`{ reason: 'rate_limited' | 'too_large' |
 * 'forbidden' | … }`) is an answer about the message, not about the
 * transport: REST would say the same, so it is passed straight on.
 *
 * Resolves with the ack `{ uid, id, server_seq }`; rejects with the error the
 * caller should treat as "failed".
 */
export function isTransportFailure(err) {
  return err instanceof Error && (err.message === 'not joined' || err.message === 'timeout');
}

export async function deliverChatMessage(channelId, payload, { ws = sendChatMessage, rest = (id, p) => chatService.sendMessage(id, p) } = {}) {
  try {
    return await ws(channelId, payload);
  } catch (err) {
    if (!isTransportFailure(err)) throw err;
    return rest(channelId, payload);
  }
}
